/**
 * Iter 9.37 - Trial countdown badge + Upgrade modal.
 *
 * Renders:
 *  - Compact badge in HostDashboard top-right showing trial days left
 *  - Modal that opens on cap-hit (402 response) or click
 */
import { useEffect, useState } from "react";
import { Sparkles, X, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

export function useTierInfo() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    const fetchTier = async () => {
      try {
        const r = await fetch(`${API}/me/tier`, { headers: auth() });
        if (r.ok) setInfo(await r.json());
      } catch { /* silent */ }
    };
    fetchTier();
    const t = setInterval(fetchTier, 60_000);
    return () => clearInterval(t);
  }, []);
  return info;
}

export function TrialBadge({ tierInfo, onClick }) {
  if (!tierInfo) return null;
  if (tierInfo.is_demo || tierInfo.is_legacy_pilot) return null;
  if (tierInfo.tier !== "free_pilot") return null;

  const status = tierInfo.trial_status;
  const days = tierInfo.trial_days_left;

  const styles = {
    trialing:    "bg-emerald-50 text-emerald-700 border-emerald-200",
    ending_soon: "bg-amber-50 text-amber-700 border-amber-200",
    expired:     "bg-red-50 text-red-700 border-red-200",
  };
  const label = status === "expired"
    ? "Trial expired"
    : status === "ending_soon"
      ? `${days}d trial left`
      : `${days}d trial`;

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border rounded-full hover:opacity-80 ${styles[status] || styles.trialing}`}
      data-testid="trial-badge"
    >
      <Sparkles className="w-3 h-3" />
      <span>{label}</span>
    </button>
  );
}

export function UpgradeModal({ open, onClose, reason }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="upgrade-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold">Upgrade to keep growing</h3>
              <p className="text-xs text-gray-500">Unlock unlimited clients + premium features</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="upgrade-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {reason && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-800">{reason}</p>
          </div>
        )}

        <div className="space-y-3 mb-6">
          <Plan name="Solo" price="$29/mo" features={["Unlimited clients", "All integrations", "Priority support"]} highlight={true} />
          <Plan name="Team" price="$79/mo" features={["Everything in Solo", "Up to 3 hosts", "Shared client roster"]} />
          <Plan name="Annual Solo" price="$280/yr" tag="Save 20%" features={["Solo features", "Annual billing", "$28 off"]} />
        </div>

        <Button
          className="w-full bg-purple-600 hover:bg-purple-700"
          onClick={() => { window.location.href = "/me?tab=settings&section=billing"; }}
          data-testid="upgrade-modal-cta"
        >
          Continue to billing
        </Button>
      </div>
    </div>
  );
}

function Plan({ name, price, features, highlight, tag }) {
  return (
    <div className={`p-3 rounded-xl border ${highlight ? "border-purple-300 bg-purple-50" : "border-gray-200 bg-white"}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm">{name}</span>
          {tag && <span className="px-1.5 py-0.5 text-[9px] font-bold bg-emerald-100 text-emerald-700 rounded-full">{tag}</span>}
        </div>
        <span className="text-sm font-bold">{price}</span>
      </div>
      <ul className="text-[11px] text-gray-600 space-y-0.5">
        {features.map((f) => <li key={f}>• {f}</li>)}
      </ul>
    </div>
  );
}
